import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useInView } from 'framer-motion'
import { ResponsiveObject } from 'react-slick'
import { Image } from '@/atoms/Image'
import { AsH1, TitleLG } from '@/atoms/Text'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { paths } from '@/constants/paths'
import { HorizontalItemScroller } from '@/molecules/HorizontalItemScroller'
import { PlaceholderMediaCard } from '@/molecules/MediaCard'
import { ComingSoonNodeSimplified, getSimplifiedComingSoonCollection } from '@/services/ComingSoonService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { isDefined } from '@/utils/primitives/generics'
import { useTranslate } from '@/utils/translate/translate-client'

const defaultResponsiveSlick: ResponsiveObject[] = [
  {
    breakpoint: 1536,
    settings: {
      slidesToScroll: 6,
      slidesToShow: 6,
    },
  },
  {
    breakpoint: 1280,
    settings: {
      slidesToScroll: 5,
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 1024,
    settings: {
      slidesToScroll: 4,
      slidesToShow: 4,
    },
  },
  {
    breakpoint: 768,
    settings: {
      slidesToScroll: 3,
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 640,
    settings: {
      slidesToScroll: 2,
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 480,
    settings: {
      slidesToScroll: 2,
      slidesToShow: 2,
    },
  },
]

export const GuildUpcomingRail: React.FC<{ landing?: boolean }> = ({ landing = false }) => {
  const { t } = useTranslate('common')
  const router = useSafeRouter()
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true })
  const [isLoaded, setIsLoaded] = useState(false)
  const [comingSoonProjects, setComingSoonProjects] = useState<ComingSoonNodeSimplified[]>([])

  useEffect(() => {
    if (isLoaded || !inView) return

    const fetchComingSoonProjects = async () => {
      try {
        const response = await getSimplifiedComingSoonCollection()
        const definedProjects = response.filter(isDefined)
        setComingSoonProjects(definedProjects)
      } catch (error) {
        logger().warn('Error fetching coming soon projects for GuildUpcomingRail', error)
      } finally {
        setIsLoaded(true)
      }
    }

    fetchComingSoonProjects()
  }, [isLoaded, inView])

  const handleCardClick = useCallback(
    (item: ComingSoonNodeSimplified) => {
      if (!landing) return
      if (!item) return

      const projectType = item.watchable?.__typename
      const slug = item.project?.slug

      if (projectType === 'ContentSpecial') {
        router.push(paths.shows.dryBar)
        return
      }

      if (!slug) return

      const base = projectType === 'ContentMovie' ? paths.movies.index : paths.shows.index
      const normalizedBase = base.startsWith('/') ? base : `/${base}`
      router.push(`${normalizedBase}/${slug}`)
    },
    [landing, router],
  )

  if (isLoaded && !comingSoonProjects?.length) return null

  return (
    <div ref={ref}>
      <header>
        <TitleLG
          color="white"
          weight="bold"
          as={AsH1}
          className={classNames('mb-3 md:mb-4', { 'photon-title-md !mb-8': landing })}
        >
          {landing ? t('comingSoon', 'Coming Soon') : t('upcoming', 'Upcoming')}
        </TitleLG>
      </header>

      <HorizontalItemScroller slidesToShow={6} slidesToScroll={6} responsive={defaultResponsiveSlick} arrows>
        {!isLoaded
          ? Array(7)
              .fill(0)
              .map((_v, i) => (
                <PlaceholderMediaCard
                  className="mr-4"
                  borderRadiusClassName="rounded-lg"
                  hideTextPlaceholders
                  orientation="portrait"
                  key={i}
                />
              ))
          : comingSoonProjects?.map((item: ComingSoonNodeSimplified) => {
              if (!item) return null
              const image =
                item?.watchable?.portraitAngelImage?.cloudinaryPath ?? item.watchable?.posterTitleImage?.cloudinaryPath
              if (!image) return null

              const title = item.watchable.name ?? ''

              const header = item.header || t('toBeAnnounced', 'To Be Announced')

              return (
                <div key={item.id}>
                  <div
                    className={classNames('mb-2 mr-4', {
                      'cursor-pointer duration-300 ease-in-out hover:z-10 md:hover:scale-[1.05]': landing,
                    })}
                    onClick={() => handleCardClick(item)}
                  >
                    <div className="block h-fit">
                      <Image
                        aria-label={title}
                        alt={title}
                        loading="lazy"
                        src={getCloudinaryImageUrl({
                          path: image,
                          width: 300,
                          height: 450,
                        })}
                        height={450}
                        width={300}
                        className="rounded-lg bg-black"
                      />
                    </div>
                  </div>
                  <TitleAndSubtitleDisplay subtitleClassName="!text-core-gray-300" className="mr-4" title={header} />
                </div>
              )
            })}
      </HorizontalItemScroller>
    </div>
  )
}
